import {
  add,
  parse,
  isMatch,
} from 'date-fns';
import {
  formatInTimeZone,
  zonedTimeToUtc,
} from 'date-fns-tz';

const BACKEND_FORMAT = 'yyyyMMdd_HHmmss';
const PRETTY_FORMAT = 'MMM d, yyyy h:mm aaa';

// in miliseconds
const units = {
  year: 24 * 60 * 60 * 1000 * 365,
  month: (24 * 60 * 60 * 1000 * 365) / 12,
  day: 24 * 60 * 60 * 1000,
  hour: 60 * 60 * 1000,
  minute: 60 * 1000,
  second: 1000,
};

const RTF = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

export function defaultTimeZone() {
  if (typeof Intl === 'undefined' || !Intl.supportedValuesOf) {
    return 'Europe/London';
  }
  const zones = Intl.supportedValuesOf('timeZone');
  if (zones.includes('Europe/London')) {
    return 'Europe/London';
  }
  return zones[0] || 'Europe/London';
}

export function defaultTimeOfDay(hours, minutes) {
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d;
}

export default {
  methods: {
    defaultTimeZone,
    defaultTimeOfDay,
    addToDate(dt, delta) {
      return add(dt, delta);
    },
    formatDate(dt) {
      return formatInTimeZone(this.getDate(dt), 'UTC', BACKEND_FORMAT);
    },
    overrideDateComponent(dt, newDate) {
      const updatedDt = new Date(dt); // clone
      updatedDt.setFullYear(newDate.getFullYear());
      updatedDt.setMonth(newDate.getMonth()); // 0-based
      updatedDt.setDate(newDate.getDate());
      return updatedDt;
    },
    getRelativeTime(dt) {
      const now = new Date();
      // Negative nums give "ago"
      const elapsed = dt - now;

      const unitKeys = Object.keys(units);
      for (let i = 0; i < unitKeys.length; i++) {
        const u = unitKeys[i];
        if ((Math.abs(elapsed) > units[u]) || u === 'second') {
          return RTF.format(Math.round(elapsed / units[u]), u);
        }
      }
      return undefined;
    },
    getDate(dt, customMask) {
      if (customMask) {
        return parse(dt, customMask, new Date());
      }
      if (isMatch(dt, BACKEND_FORMAT)) {
        // Backend format is always UTC
        // Add Timezone info
        return parse(`${dt}Z`, `${BACKEND_FORMAT}X`, new Date());
      }
      return new Date(dt);
    },
    msSinceEpoch(dt) {
      if (!dt) {
        return null;
      }
      return this.getDate(dt).getTime();
    },
    getDateInUtc(dt, timezone) {
      return zonedTimeToUtc(dt || new Date(), timezone);
    },

    dateIsPast(dt) {
      if (this.getDate(dt) <= Date.now()) {
        return true;
      }
      return false;
    },

    dateIsNow(dt) {
      const now = Date.now();
      const date = this.getDate(dt);
      // Check if the date is within 1 second of now to avoid timezone issues
      return Math.abs(date - now) < 1000;
    },

    dateIsNearNow(dt) {
      const now = Date.now();
      const date = this.getDate(dt);
      // Check if the date is within 2 weeks from now and not in the past
      return date >= now && date <= now + 1000 * 60 * 60 * 24 * 14;
    },

    dateWithinRange(now, start, end) {
      return now >= this.getDate(start) && now <= this.getDate(end);
    },

    parseTimestamp(stamp, customMask, customFormat, timezone) {
      if (!stamp) {
        return '';
      }
      const dt = stamp instanceof Date ? stamp : this.getDate(stamp, customMask);
      const actualTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      return formatInTimeZone(dt, actualTimezone, customFormat || PRETTY_FORMAT);
    },

    // Parse timestamp to time only
    parseTimestampToTime(stamp, customMask, format, timezone) {
      if (!stamp) {
        return '';
      }
      const dt = stamp instanceof Date ? stamp : this.getDate(stamp, customMask);
      const actualTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      return formatInTimeZone(dt, actualTimezone, format || 'HH:mm');
    },

    parseBulkTimestamps(stamps, customMask) {
      const output = [];
      for (let i = 0; i < stamps.length; i++) {
        output.push(this.parseTimestamp(stamps[i], customMask));
      }
      return output;
    },
    /**
     * Formats a date string into a specific format.
     * @param {string} dateString - The date string to format.
     * @returns {string} The formatted date string or an empty string if dateString is falsy.
     */
    formatDateString(dateString) {
      if (dateString) {
        return this.parseTimestamp(this.getDate(dateString), undefined, 'MMM d, yyyy');
      }
      return '';
    },

    formatTimeSpent(minutes) {
      const minutesValue = Math.round(minutes ?? 0); // Round to nearest minute
      const hours = Math.floor(minutesValue / 60);
      const remainingMinutes = minutesValue % 60;
      if (hours === 0) {
        return `${remainingMinutes}m`;
      }
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}m`;
    },

    isValidDatetime(dt) {
      if (dt instanceof Date) {
        return true;
      }
      return isMatch(dt, BACKEND_FORMAT);
    },
  },
};
